/**
 * F13 — Quién puede inscribirse a un evento.
 *
 * Manda la EDAD, no la sección guardada en la ficha (2026-08-10).
 *
 * Antes se miraba `staffSection ?? section`, un dato que cualquier director
 * puede cambiar editando el perfil: bastaba con marcar a un conquistador de 12
 * años como "aventureros" para colarlo al campamento de Aventureros. Ahora la
 * sección guardada no abre ninguna puerta que la edad no abra.
 *
 * El criterio, decidido con la Asociación:
 *   · 3 a 9 años   → Aventureros
 *   · 10 a 15      → Conquistadores
 *   · 16 o más     → adultos; por edad cuentan como Guías Mayores, y además su
 *                    cargo les da acceso a un evento de la sección que dirigen
 *                    (si no, el staff adulto de Conquistadores no podría ir a
 *                    un evento de Conquistadores).
 *   · menos de 3   → no hay sección; no se pueden inscribir a nada.
 *
 * La edad se calcula el día de la INSCRIPCIÓN, no el del evento: un niño de 9
 * que cumple 10 tres semanas antes del campamento no pierde su última
 * oportunidad de ir con los Aventureros.
 *
 * Encima de eso vive la excepción de transición: los que cumplen 10 en el
 * primer semestre pasan a Conquistadores por edad y se quedaban sin ir nunca al
 * campamento de Aventureros. La Asociación les da esa última oportunidad, pero
 * SIN moverlos de sección, y sólo si no alcanzaron a ir al campamento de la
 * suya.
 */
import type { Club, ClubType, Event, Member } from "@/types";
import { calculateAge } from "@/lib/utils";

/** El modal trabaja con MemberWithPerson; la fecha de nacimiento vive en la
 *  Person anidada. Se acepta opcional para no acoplar al tipo de la vista. */
type MemberForEligibility = Member & {
  person?: { birthDate?: unknown } | null;
};

export interface EligibilityResult {
  eligible: boolean;
  /** true cuando entra por la excepción, no por su sección. */
  byException: boolean;
  /** Etiqueta a mostrar junto al nombre cuando entra por excepción. */
  label?: string;
}

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const withToDate = v as { toDate?: () => Date };
  if (typeof withToDate?.toDate === "function") {
    const d = withToDate.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(v as string);
  return isNaN(d.getTime()) ? null : d;
}

/** Sección efectiva del miembro (la de staff manda sobre la etaria). */
export function memberSection(member: MemberForEligibility): ClubType | undefined {
  return (member.staffSection ?? member.section) as ClubType | undefined;
}

/** A partir de esta edad se es adulto: el cargo también cuenta. */
const EDAD_ADULTO = 16;

/**
 * Sección que corresponde a una edad. Es la fuente de verdad para inscribir:
 * no depende de ningún campo editable.
 *
 * Ojo con el borde de los 10 años: aquí es Conquistadores a secas. Que un niño
 * de 10 pueda ir al campamento de Aventureros lo decide la excepción de
 * transición del evento (fecha de nacimiento + no haber ido al campamento de
 * Conquistadores), no este mapeo — si se metiera el solapamiento aquí,
 * entrarían también los que ya fueron, que es justo lo que la regla evita.
 */
export function sectionByAge(age: number): ClubType | undefined {
  if (age >= 3 && age <= 9) return "aventureros";
  if (age >= 10 && age <= 15) return "conquistadores";
  if (age >= EDAD_ADULTO) return "guias_mayores";
  return undefined; // menores de 3 años
}

/**
 * ¿Este miembro puede inscribirse a este evento?
 *
 * @param registeredPersonIdsInExcludedEvent  personIds ya inscritos en el
 *   evento excluyente (el campamento de su propia sección). Se pasa desde
 *   fuera para no consultar Firestore dentro de un bucle de render.
 */
export function checkEventEligibility(
  member: MemberForEligibility,
  event: Pick<Event, "clubTypesAllowed" | "transitionalAgeException">,
  registeredPersonIdsInExcludedEvent?: Set<string>,
): EligibilityResult {
  const allowed = event.clubTypesAllowed?.length
    ? event.clubTypesAllowed
    : null;
  const section = memberSection(member);

  // Sin restricción de secciones: pasa todo el mundo (eventos legacy).
  if (!allowed) return { eligible: true, byException: false };

  const birth = toDate(member.person?.birthDate ?? undefined);

  // Sin fecha de nacimiento no hay edad que valga: se cae al criterio viejo
  // (la sección guardada) para no dejar a nadie fuera por un dato faltante.
  // Hoy en producción no hay ninguno, pero una ficha importada podría no
  // tenerla.
  if (!birth) {
    return section && allowed.includes(section)
      ? { eligible: true, byException: false }
      : { eligible: false, byException: false };
  }

  const age = calculateAge(birth);
  const seccionEtaria = sectionByAge(age);

  // La edad abre la puerta.
  if (seccionEtaria && allowed.includes(seccionEtaria))
    return { eligible: true, byException: false };

  // A los adultos, además, su cargo les da acceso al evento de la sección que
  // dirigen: sin esto, el staff de Conquistadores no podría inscribirse a un
  // evento de Conquistadores (por edad todos son Guías Mayores). En los
  // menores el cargo NO cuenta: ahí la edad es la única fuente de verdad.
  if (age >= EDAD_ADULTO && section && allowed.includes(section))
    return { eligible: true, byException: false };

  const ex = event.transitionalAgeException;
  if (!ex?.enabled) return { eligible: false, byException: false };
  // `fromSections` se compara contra la sección POR EDAD, no la guardada: si
  // no, marcar a un niño en otra sección volvería a decidir su elegibilidad.
  if (!seccionEtaria || !ex.fromSections?.includes(seccionEtaria))
    return { eligible: false, byException: false };

  // Comparación por día completo: `bornTo` incluye ese día.
  const from = new Date(`${ex.bornFrom}T00:00:00`);
  const to = new Date(`${ex.bornTo}T23:59:59`);
  if (isNaN(from.getTime()) || isNaN(to.getTime()))
    return { eligible: false, byException: false };
  if (birth < from || birth > to)
    return { eligible: false, byException: false };

  // Si ya fue al campamento de su propia sección, no repite etapa.
  if (
    ex.excludeIfRegisteredInEventId &&
    member.personId &&
    registeredPersonIdsInExcludedEvent?.has(member.personId)
  ) {
    return { eligible: false, byException: false };
  }

  return {
    eligible: true,
    byException: true,
    label: ex.label ?? "Edad de transición",
  };
}

/** Resumen para explicar la excepción en la UI. */
export function describeException(
  event: Pick<Event, "transitionalAgeException">,
  clubName?: Club["name"],
): string | null {
  const ex = event.transitionalAgeException;
  if (!ex?.enabled) return null;
  const fmt = (iso: string) => {
    const d = new Date(`${iso}T12:00:00`);
    return isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" });
  };
  return (
    `Además de las secciones habilitadas, pueden inscribirse los miembros ` +
    `nacidos entre el ${fmt(ex.bornFrom)} y el ${fmt(ex.bornTo)}` +
    `${clubName ? ` de ${clubName}` : ""} que este año pasaron de sección por edad ` +
    `y no alcanzaron a ir al campamento de la suya.`
  );
}
